import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Box, SxProps, Theme } from '@mui/system';
import CustomAccordion from 'components/shared/custom_accordion';
import { FieldArray, Formik } from 'formik';
import { formatCNPJ } from 'services/utils/cnpj.utils';
import formValidation from './validations.form';
import { formatCep } from 'services/utils/cep.utils';
import { ContactDto } from 'common/dtos/contact.dto';
import { formatPhoneNumber } from 'services/utils/cellphone.utils';
import {
  InstitutionContactTypeEnum,
  InstitutionStatusEnum,
} from 'common/enums';
import { IModel, IState } from './types.form';
import { isArray } from 'lodash';
import { useAuth } from 'contexts/auth/auth_context';


const lineStyle: SxProps<Theme> | undefined = {
  display: 'flex',
};

const columnStyle: SxProps<Theme> | undefined = {
  width: '50%',
  padding: '8px',
};

const fieldStyle: SxProps<Theme> | undefined = {
  width: '100%',
};

const getContactKey = (contact: ContactDto, index: number) => {
  if (contact.uuid && contact.uuid !== '--') {
    return contact.uuid;
  }
  return `contact-${contact.type}-${index}`;
};

// Export helper functions for unit testing (no runtime behavior change)
export { getContactKey };

export const createFieldChangeHandlerFactory = (
  fieldPath: string,
  handleChange: any,
  setStateOnFormikChange: any,
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  handleChange(e);
  setStateOnFormikChange(fieldPath, e.target.value);
};

export const createContactChangeHandlerFactory = (
  index: number,
  setFieldValue: any,
  setStateOnFormikChange: any,
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const fieldPath = `institution.contactList[${index}].value`;
  setFieldValue(fieldPath, e.target.value);
  setStateOnFormikChange(fieldPath, e.target.value);
};

export const handleAddStatusFactory = (setToAddStatusList: any) => (status: InstitutionStatusEnum) => () => {
  setToAddStatusList(status);
};

export const handleDelStatusFactory = (setToDelStatusList: any) => (status: InstitutionStatusEnum) => () => {
  setToDelStatusList(status);
};

export const checkFormValuesFn = async (values: IState) => {
  try {
    await formValidation.validate(values, { abortEarly: false });
    return { valid: true, errors: {} };
  } catch (err: any) {
    const errors = err.inner.reduce((acc: any, validationError: any) => {
      acc[validationError.path] = validationError.message;
      return acc;
    }, {});
    return { valid: false, errors };
  }
};

const InstitutionFormStepOne: React.FC<
  Pick<
    IModel,
    | 'state'
    | 'setToAddStatusList'
    | 'setToDelStatusList'
    | 'setHasError'
    | 'setStateOnFormikChange'
  >
> = ({
  state,
  setToAddStatusList,
  setToDelStatusList,
  setHasError,
  setStateOnFormikChange,
}) => {
    const createFieldChangeHandler = (
      fieldPath: string,
      handleChange: any,
      setStateOnFormikChange: any
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e);
      setStateOnFormikChange(fieldPath, e.target.value);
    };

    const createContactChangeHandler = (
      index: number,
      setFieldValue: any,
      setStateOnFormikChange: any
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const fieldPath = `institution.contactList[${index}].value`;
      setFieldValue(fieldPath, e.target.value);
      setStateOnFormikChange(fieldPath, e.target.value);
    };

    const handleAddStatus = (status: InstitutionStatusEnum) => () => {
      setToAddStatusList(status);
    };

    const handleDelStatus = (status: InstitutionStatusEnum) => () => {
      setToDelStatusList(status);
    };

    const checkFormValues: any = (values: IState) => {
      return formValidation
        .validate(values, { abortEarly: false })
        .then(() => {
          setHasError(false);
          return {};
        })
        .catch((err) => {
          const errors = err.inner.reduce((acc: any, validationError: any) => {
            acc[validationError.path] = validationError.message;
            return acc;
          }, {});
          console.log('Validation errors:', errors); // Added for debugging
          setHasError(true);
          return errors;
        });
    };

    const allowedPermissions = [
      'ADMINISTRADOR',
      'GERENTE ESTADUAL',
      'OPERADOR ESTADUAL',
      'CONSELHEIRO ESTADUAL',
    ];

    const { user } = useAuth();
    const hasAllowedPermission = allowedPermissions.includes(
      user?.permission?.name ?? '',
    );

    if (state.institution?.contactList.length === 0) {
      state.institution?.contactList.push({
        uuid: '--',
        type: InstitutionContactTypeEnum.CELLPHONE,
        value: '',
      });
    }
    return (
      <Formik
        enableReinitialize
        onSubmit={() => { }}
        initialValues={state}
        validationSchema={formValidation}
        validate={checkFormValues}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => {
          return (
            <Box sx={{ mx: '32px', mt: '32px' }}>
              <CustomAccordion accordionSummaryText='Dados cadastrais'>
                <Box>
                  <Box sx={lineStyle}>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.fantasyName'
                        label='Nome Fantasia'
                        required={true}
                        variant='outlined'
                        size='small'
                        value={values.institution?.fantasyName}
                        onChange={createFieldChangeHandler(
                          'institution.fantasyName',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.fantasyName &&
                          !!errors.institution?.fantasyName
                        }
                        helperText={
                          touched.institution?.fantasyName &&
                          errors.institution?.fantasyName
                        }
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.identification'
                        required={true}
                        label='CNPJ'
                        variant='outlined'
                        size='small'
                        value={formatCNPJ(state.institution?.identification)}
                        inputProps={{ maxLength: 18 }}
                        onChange={createFieldChangeHandler(
                          'institution.identification',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.identification &&
                          !!errors.institution?.identification
                        }
                        helperText={
                          touched.institution?.identification &&
                          errors.institution?.identification
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={lineStyle}>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.name'
                        required={true}
                        label='Razão Social'
                        variant='outlined'
                        size='small'
                        value={values.institution?.name}
                        onChange={createFieldChangeHandler(
                          'institution.name',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.name && !!errors.institution?.name
                        }
                        helperText={
                          touched.institution?.name && errors.institution?.name
                        }
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.address.cep'
                        label='CEP'
                        required={true}
                        variant='outlined'
                        size='small'
                        value={formatCep(values.institution?.address?.zipcode)}
                        inputProps={{ maxLength: 9 }}
                        onChange={createFieldChangeHandler(
                          'institution.address.cep',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.address?.zipcode &&
                          !!errors.institution?.address?.zipcode
                        }
                        helperText={
                          touched.institution?.address?.zipcode &&
                          errors.institution?.address?.zipcode
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={lineStyle}>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.address.street'
                        label='Endereço'
                        required={true}
                        variant='outlined'
                        size='small'
                        value={values.institution?.address?.street}
                        onChange={createFieldChangeHandler(
                          'institution.address.street',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.address?.street &&
                          !!errors.institution?.address?.street
                        }
                        helperText={
                          touched.institution?.address?.street &&
                          errors.institution?.address?.street
                        }
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.address.number'
                        label='Número'
                        variant='outlined'
                        size='small'
                        value={values.institution?.address?.number}
                        onChange={createFieldChangeHandler(
                          'institution.address.number',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.address?.number &&
                          !!errors.institution?.address?.number
                        }
                        helperText={
                          touched.institution?.address?.number &&
                          errors.institution?.address?.number
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={lineStyle}>
                    <Box sx={columnStyle}>
                      <TextField
                        sx={fieldStyle}
                        name='institution.email'
                        label='Email'
                        variant='outlined'
                        size='small'
                        value={values.institution?.email}
                        onChange={createFieldChangeHandler(
                          'institution.email',
                          handleChange,
                          setStateOnFormikChange
                        )}
                        onBlur={handleBlur}
                        error={
                          touched.institution?.email &&
                          !!errors.institution?.email
                        }
                        helperText={
                          touched.institution?.email && errors.institution?.email
                        }
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                  </Box>
                  <Box sx={lineStyle}>
                    <FieldArray
                      name='institution.contactList'
                      render={() => {
                        if (values.institution?.contactList.length > 0) {
                          return values.institution?.contactList?.map(
                            (contact, index: any) => (
                              <Box key={getContactKey(contact, index)} sx={columnStyle}>
                                <TextField
                                  sx={fieldStyle}
                                  inputProps={{ maxLength: 19 }}
                                  label={`Telefone 0${index + 1}`}
                                  name={`institution.contactList[${index}].value`}
                                  variant='outlined'
                                  size='small'
                                  value={formatPhoneNumber(
                                    values.institution?.contactList[index].value,
                                  )}
                                  onChange={createContactChangeHandler(
                                    index,
                                    setFieldValue,
                                    setStateOnFormikChange
                                  )}
                                  onBlur={handleBlur}
                                  error={
                                    touched.institution?.contactList?.[index]
                                      ?.value &&
                                    !!(
                                      errors.institution?.contactList?.[
                                      index
                                      ] as ContactDto
                                    )?.value
                                  }
                                  helperText={
                                    ''
                                  }
                                />
                              </Box>
                            ),
                          );
                        }

                        return (
                          <Box key={1} sx={columnStyle}>
                            <TextField
                              sx={fieldStyle}
                              inputProps={{ maxLength: 11 }}
                              label={`Telefone 0${1}`}
                              name={`institution.contactList[${1}].value`}
                              variant='outlined'
                              size='small'
                              value={''}
                              onChange={createContactChangeHandler(
                                1,
                                setFieldValue,
                                setStateOnFormikChange
                              )}
                              onBlur={handleBlur}
                              error={true}
                              helperText={
                                ''
                                // MSG_HELPER_CELLPHONE
                              }
                            />
                          </Box>
                        );
                      }}
                    />
                  </Box>
                </Box>
              </CustomAccordion>

              <CustomAccordion accordionSummaryText='Dados administrativos'>
                <Box>
                  <Box sx={lineStyle}>
                    <Box sx={columnStyle}>
                      <Autocomplete
                        disablePortal
                        isOptionEqualToValue={(value, option) =>
                          option.id === value.id
                        }
                        options={state.groupList?.map((item) => ({
                          id: item.uuid,
                          label: item.name,
                          description: item.description,
                        }))}
                        value={{
                          id: values.institution?.profile?.group?.uuid,
                          label: values.institution?.profile?.group?.name,
                          description:
                            values.institution?.profile?.group?.description,
                        }}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            const data = {
                              uuid: newValue.id,
                              name: newValue.label,
                              description: newValue?.description as string,
                            };
                            setFieldValue('institution.profile.group', data);
                            setStateOnFormikChange(
                              'institution.profile.group',
                              data,
                            );
                          }
                        }}
                        onBlur={handleBlur}
                        renderInput={(params) => (
                          <TextField {...params} label='Grupo da instituição' />
                        )}
                      />
                    </Box>
                    <Box sx={columnStyle}>
                      <Autocomplete
                        disablePortal
                        isOptionEqualToValue={(value, option) =>
                          option.id === value.id
                        }
                        options={
                          state.profileList && isArray(state.profileList)
                            ? state.profileList?.map((item) => ({
                              id: item.uuid,
                              label: item.name,
                              description: item.description,
                            }))
                            : []
                        }
                        value={{
                          id: values.institution?.profile?.uuid,
                          label: values.institution?.profile?.name,
                          description: values.institution?.profile?.description,
                        }}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            const data = {
                              uuid: newValue.id,
                              name: newValue.label,
                              description: newValue.description,
                            };
                            setFieldValue('institution.profile', {
                              ...values.institution?.profile,
                              ...data,
                            });
                            setStateOnFormikChange('institution.profile', {
                              ...values.institution?.profile,
                              ...data,
                            });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label='Perfil da instituição' />
                        )}
                      />
                    </Box>
                  </Box>

                  {!!values.institution.statusList?.length &&
                    hasAllowedPermission && (
                      <Box sx={lineStyle}>
                        <Box sx={{ ...columnStyle, width: 'auto' }}>
                          <FormControl>
                            <FormLabel id='demo-radio-buttons-group-label'>
                              Cadastro Ativo:
                            </FormLabel>
                            <RadioGroup
                              name='radio-buttons-group'
                              aria-labelledby='demo-radio-buttons-group-label'
                              defaultValue={() =>
                                values.institution?.statusList?.some(
                                  (item) =>
                                    item.name ===
                                    InstitutionStatusEnum.REGISTER_ACTIVE,
                                )
                              }
                            >
                              <FormControlLabel
                                value={true}
                                label='Sim'
                                control={<Radio />}
                                onClick={handleAddStatus(InstitutionStatusEnum.REGISTER_ACTIVE)}
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                onClick={handleDelStatus(InstitutionStatusEnum.REGISTER_ACTIVE)}
                                label='Não'
                              />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                        <Box sx={{ ...columnStyle, width: 'auto' }}>
                          <FormControl>
                            <FormLabel id='demo-radio-buttons-group-label'>
                              Bloquear Cadastro:
                            </FormLabel>
                            <RadioGroup
                              aria-labelledby='demo-radio-buttons-group-label'
                              name='radio-buttons-group'
                              defaultValue={() =>
                                values.institution?.statusList?.some(
                                  (item) =>
                                    item.name ===
                                    InstitutionStatusEnum.REGISTER_BLOCKED,
                                )
                              }
                            >
                              <FormControlLabel
                                value={true}
                                control={<Radio />}
                                label='Sim'
                                onClick={handleAddStatus(InstitutionStatusEnum.REGISTER_BLOCKED)}
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                label='Não'
                                onClick={handleDelStatus(InstitutionStatusEnum.REGISTER_BLOCKED)}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    )}

                  {!values.institution?.statusList?.length &&
                    hasAllowedPermission && (
                      <Box sx={lineStyle}>
                        <Box sx={{ ...columnStyle, width: 'auto' }}>
                          <FormControl>
                            <FormLabel id='demo-radio-buttons-group-label'>
                              Cadastro Ativo:
                            </FormLabel>
                            <RadioGroup
                              name='radio-buttons-group'
                              aria-labelledby='demo-radio-buttons-group-label'
                              defaultValue={() =>
                                values.institution?.statusList?.some(
                                  (item) =>
                                    item.name ===
                                    InstitutionStatusEnum.REGISTER_ACTIVE,
                                )
                              }
                            >
                              <FormControlLabel
                                value={true}
                                control={<Radio />}
                                label='Sim'
                                onClick={handleAddStatus(InstitutionStatusEnum.REGISTER_ACTIVE)}
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                label='Não'
                                onClick={handleDelStatus(InstitutionStatusEnum.REGISTER_ACTIVE)}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                        <Box sx={{ ...columnStyle, width: 'auto' }}>
                          <FormControl>
                            <FormLabel id='demo-radio-buttons-group-label'>
                              Bloquear Cadastro:
                            </FormLabel>
                            <RadioGroup
                              aria-labelledby='demo-radio-buttons-group-label'
                              name='radio-buttons-group'
                              defaultValue={() =>
                                values.institution.statusList?.some(
                                  (item) =>
                                    item.name ===
                                    InstitutionStatusEnum.REGISTER_BLOCKED,
                                )
                              }
                            >
                              <FormControlLabel
                                value={true}
                                control={<Radio />}
                                label='Sim'
                                onClick={handleAddStatus(InstitutionStatusEnum.REGISTER_BLOCKED)}
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                label='Não'
                                onClick={handleDelStatus(InstitutionStatusEnum.REGISTER_BLOCKED)}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    )}
                </Box>
              </CustomAccordion>
            </Box>
          );
        }}
      </Formik>
    );
  };

export default InstitutionFormStepOne;
